import React from 'react';
import { Award, Target, Users, TrendingUp } from 'lucide-react';
import { companyInfo, images } from '../data/mock';
import { PageSEO } from '../components/PageSEO';

const About = () => {
  const features = [
  {
    icon: Award,
    title: "Experiencia Comprobada",
    description: "Años de experiencia en la distribución de productos de consumo masivo en Ecuador"
  },
  {
    icon: Target,
    title: "Enfoque Estratégico",
    description: "Cobertura regional efectiva con planes de expansión en provincias clave"
  },
  {
    icon: Users,
    title: "Equipo Profesional",
    description: "Fuerza de ventas capacitada y comprometida con el éxito de nuestras marcas"
  },
  {
    icon: TrendingUp,
    title: "Crecimiento Sostenible",
    description: "Operaciones eficientes que generan valor para todas las partes involucradas"
  }];


  return (
    <div className="min-h-screen pt-20">
      <PageSEO path="/nosotros" />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Quiénes Somos
            </h1>
            <p className="text-xl sm:text-2xl text-emerald-100 leading-relaxed">
              Conectando marcas líderes con puntos de venta en todo el centro del Ecuador
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
                Nuestra Historia
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {companyInfo.fullName}
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Somos una empresa de distribución especializada en productos de consumo masivo. Nuestra misión es conectar las mejores marcas 
                  con cada punto de venta en la zona centro del Ecuador.
                </p>
                <p>
                  Nos enfocamos en tres pilares fundamentales: <strong className="text-gray-900">cobertura regional efectiva</strong>, 
                  <strong className="text-gray-900"> logística eficiente</strong> y una 
                  <strong className="text-gray-900"> fuerza de ventas altamente capacitada</strong>. Esta combinación 
                  nos permite garantizar la presencia constante de los productos de nuestras marcas aliadas.
                </p>
                <p>
                  Trabajamos tanto con <strong className="text-gray-900">canales tradicionales</strong> (tiendas de barrio, 
                  mercados locales) como con <strong className="text-gray-900">canales modernos</strong> (supermercados, 
                  cadenas retail), adaptando nuestras estrategias a las necesidades específicas de cada segmento.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={images.team}
                alt="Equipo Grupo BG"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />

              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl -z-10"></div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-gradient-to-br from-gray-50 to-emerald-50 hover:shadow-lg transition-all duration-300 group">

                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>);

            })}
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
              <h3 className="text-2xl font-bold mb-4">Nuestra Misión</h3>
              <p className="text-emerald-50 leading-relaxed">"Brindar un servicio de distribución eficiente y confiable de productos de consumo masivo en la zona centro del Ecuador, ofreciendo cobertura a mayoristas y canal HORECA con compromiso, cercanía y excelencia operativa. Nos enfocamos en generar valor para nuestros clientes, fortalecer las marcas que representamos y contribuir al desarrollo económico de nuestra región."



              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 text-white">
              <h3 className="text-2xl font-bold mb-4">Nuestra Visión</h3>
              <p className="text-gray-300 leading-relaxed">"Ser el principal aliado estratégico de las grandes marcas de consumo masivo en Ecuador, consolidándonos como la empresa de distribución más confiable, innovadora y de mayor cobertura a nivel nacional, destacando por nuestra calidad de servicio, solidez comercial y crecimiento sostenible."



              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <img
              src={images.warehouse}
              alt="Centro de distribución"
              className="rounded-2xl shadow-lg w-full h-80 object-cover hover:shadow-xl transition-shadow duration-300" />

            <img
              src={images.fleet}
              alt="Flota de distribución"
              className="rounded-2xl shadow-lg w-full h-80 object-cover hover:shadow-xl transition-shadow duration-300" />

          </div>
        </div>
      </section>
    </div>);

};

export default About;